import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Observable, of, Subject, switchMap, tap, finalize, catchError, map, takeUntil, debounceTime, shareReplay, combineLatest, startWith } from 'rxjs';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MainHeaderComponent } from '../shared/main-header/main-header.component';
import { LibraryObjectsSerive } from '../../services/library_objects.service';
import { ClientLibraryObjectsSerive } from '../../services/client_library_objects.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { LibraryService } from '../../services/library.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { ModelsService } from '../../services/models.service';
import { ClientsService } from '../../services/clients.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatSlideToggle, MatSlideToggleModule } from '@angular/material/slide-toggle';

export interface FilterOption {
  key: string;
  displayName: string;
  isLoading: boolean;
  hasSubFilter: boolean;
  subFilterOptions: { id: number; name: string }[];
  filteredOptions: { id: number; name: string }[];
  searchControl: FormControl;
  checked: boolean;
  selectedValue: number | null;
}

export interface LibraryObject {
  name: string; label: string; description: string | null;
  thumbnail: string | null; is_active: boolean; library_attributes: any[]; models: any[];
}

export interface Model {
  model_id: number;
  name: string;
}

interface LibraryAttribute {
  library_attribute_id: number;
  id?: number;
  name: string;
  label?: string;
  value?: unknown;
  library_attribute_type: {
    name: string;
    label: string;
  };
}

interface ClientAttributeRecord {
  client_library_object_id: number;
  client_id: number;
  model_id: number;
  name: string;
  label: string;
  sku?: string;
  library_attributes: LibraryAttribute[];
}

interface ClientListItem {
  client_id: number;
  name: string;
}


@Component({
  selector: 'app-library-objects',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule, MainHeaderComponent,
    MatButtonModule, MatCardModule, MatCheckboxModule, MatDrawerContainer,
    MatDrawer, MatFormFieldModule, MatIconModule, MatInputModule,
    MatSelectModule, MatTableModule, MatSnackBarModule, MatExpansionModule,
    MatMenuModule, MatButtonModule, MatChipsModule, NgxMatSelectSearchModule,
    MatSlideToggleModule
  ],
  templateUrl: './library-objects.component.html',
  styleUrl: './library-objects.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class LibraryObjectsComponent implements OnInit, OnDestroy {

  @ViewChild('drawer') drawer!: MatDrawer;
  private destroy$ = new Subject<void>();

  public titleHeader = 'LOI:';
  public libraryId!: number;
  public thumbnailPreview: string | ArrayBuffer | null = null;
  public fileToUpload: File | null = null;

  public isLoading = true;
  public drawerMode: 'product' | 'client' = 'product';
  public isSaving = false;
  public isLoadingClientAttributes = true;

  public clientAttributes: ClientAttributeRecord[] = [];
  public editingClientAttributeId: number | null = null;

  public libraryObjectData$!: Observable<LibraryObject>;
  public libraryForm: FormGroup;
  public clientAttributeForm: FormGroup;

  public allModels: Model[] = [];
  public filteredModels$!: Observable<Model[]>;
  public searchTerm = new FormControl('');

  public formConfig = [
    { controlName: 'name', label: 'Nombre', type: 'text' },
    { controlName: 'label', label: 'Nombre visible', type: 'text' },
  ];

  public filters: FilterOption[] = [
    { key: 'brand', displayName: 'Marca', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'category', displayName: 'Categoría', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'company', displayName: 'Compañía', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'flavor', displayName: 'Sabores', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'multi-package', displayName: 'Multi-empaque', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'package', displayName: 'Empaque', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'shape', displayName: 'Forma', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'size', displayName: 'Tamaño', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'line', displayName: 'Línea', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'presentation', displayName: 'Presentación', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'version', displayName: 'Versión', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'other', displayName: 'Otros', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'family', displayName: 'Familia', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') },
    { key: 'is_competence', displayName: 'Es competencia', isLoading: false, hasSubFilter: true, subFilterOptions: [], checked: false, selectedValue: null, filteredOptions: [], searchControl: new FormControl('') }
  ];


  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private libraryObjectsService: LibraryObjectsSerive,
    private clientLibraryObjectsService: ClientLibraryObjectsSerive,
    private snackBar: MatSnackBar,
    private router: Router,
    private libraryService: LibraryService,
    private modelsService: ModelsService,
    private clientsService: ClientsService
  ) {
    this.libraryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
      label: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
      description: [''],
      is_active: new FormControl(true),
      thumbnail: ['', [Validators.pattern('https?://.*')]],
      library_attributes: this.fb.array([]),
      models: new FormControl([])
    });

    this.clientAttributeForm = this.fb.group({
      client_id: [null, Validators.required],
      name: ['', Validators.required],
      label: [''],
      sku: [''],
      library_attributes: this.fb.array([])
    });
  }
  clientList$: Observable<{ data: ClientListItem[] }> = this.clientsService.loadClients();
  disableAddButtons = false;

  ngOnInit(): void {
    const models$ = this.modelsService.loadModelsStore().pipe(
      map(res => {
        console.log('Models loaded for store:', res.data);
        return res.data;
      }),
      tap(models => this.allModels = models),
      shareReplay(1)
    );

    const search$ = this.searchTerm.valueChanges.pipe(
      startWith(this.searchTerm.value ?? ''), // emite inicialmente
      debounceTime(200)
    );

    this.filteredModels$ = combineLatest([models$, search$]).pipe(
      map(([models, search]) => {
        const ql = (search ?? '').toString().trim().toLowerCase();
        if (!ql) return models;
        return models.filter((m: Model) => (m.name ?? '').toLowerCase().includes(ql));
      }),
      shareReplay(1)
    );

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.libraryId = +id;
      this.titleHeader = `LOI: ${this.libraryId}`;
      this.loadLibraryObjectData();
      this.loadClientAttributes(this.libraryId);
    } else {
      this.titleHeader = 'Creando producto';
      this.disableAddButtons = true;
      this.isLoading = false;
      const defaultObject: LibraryObject = {
        name: '', label: '', description: null,
        thumbnail: null, is_active: false, library_attributes: [], models: []
      };
      this.libraryObjectData$ = of(defaultObject);
      console.log('LibraryObjectData$ inicializado con objeto por defecto:', defaultObject);
    }
    this.filters.forEach(filter => {
      filter.searchControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.filterOptions(filter);
        });
    });


  }
  get activeControl(): FormControl {
    return this.libraryForm.get('is_active') as FormControl;
  }


  trackByModelId(_i: number, model: Model) { return model.model_id; }

  private filterOptions(filter: FilterOption): void {
    const searchTerm = filter.searchControl.value?.toLowerCase() || '';
    if (!searchTerm) {
      filter.filteredOptions = [...filter.subFilterOptions];
    } else {
      filter.filteredOptions = filter.subFilterOptions.filter(option =>
        option.name.toLowerCase().includes(searchTerm)
      );
    }
  }
  public getSelectedOptionName(filter: FilterOption): string {
    if (!filter.selectedValue) return '';
    const selected = filter.subFilterOptions.find(opt => opt.id === filter.selectedValue);
    return selected ? selected.name : '';
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get libraryAttributesArray(): FormArray {
    return this.libraryForm.get('library_attributes') as FormArray;
  }

  get clientLibraryAttributesArray(): FormArray {
    return this.clientAttributeForm.get('library_attributes') as FormArray;
  }


  public loadLibraryObjectData(): void {
    this.isLoading = true;
    this.libraryObjectData$ = this.libraryObjectsService.getLibraryObjectById(this.libraryId).pipe(
      tap((data: LibraryObject) => {
        if (data) {
          if (data.thumbnail === 'NULL') { data.thumbnail = ''; }
          if (data.description === 'NULL') { data.description = ''; }
          this.libraryForm.get('is_active')?.setValue(data.is_active);
          const modelIds = data.models.map(model => model.model_id);
          this.libraryForm.patchValue({ ...data, models: modelIds });
          this.libraryForm.get('name')?.disable();
          this.initializeFilters(data.library_attributes.map((attr: LibraryAttribute) => ({
            key: attr.library_attribute_type.name,
            value: attr.library_attribute_id
          })));
        }
      }),
      finalize(() => { this.isLoading = false; })
    );
  }

  private prepareSavePayload(): any {
    const formValue = this.libraryForm.getRawValue();
    const modelIds = formValue.models || [];
    const payload = {
      ...formValue,
      models: modelIds.map((id: number) => ({ model_id: id }))
    };
    return payload;
  }

  public updateObject(): void {
    const uploadObservable = this.fileToUpload
      ? this.uploadImageAndGetPath(this.fileToUpload)
      : of(this.libraryForm.get('thumbnail')?.value || null);

    uploadObservable.pipe(
      switchMap((thumbnailUrl: string | null) => {
        if (thumbnailUrl) {
          this.libraryForm.patchValue({ thumbnail: thumbnailUrl }, { emitEvent: false });
        }
        const payload = this.prepareSavePayload();
        return this.libraryObjectsService.updateLibraryObject(this.libraryId, payload);
      })
    ).subscribe({
      next: () => {
        this.snackBar.open('Cambios guardados exitosamente', 'Cerrar', { duration: 3000 });
        this.fileToUpload = null;
        this.libraryForm.markAsPristine();
        this.closeDrawer();
        this.loadLibraryObjectData();
      },
      error: () => this.snackBar.open('Error al guardar los cambios', 'Cerrar', { duration: 5000 })
    });
  }

  public createObject(): void {
    if (this.libraryForm.invalid || this.isSaving) { return; }
    this.isSaving = true;

    const payload = this.prepareSavePayload();
    this.libraryObjectsService.createLibraryObject(payload).pipe(
      finalize(() => { this.isSaving = false; })
    ).subscribe({
      next: (newObject: any) => {
        this.snackBar.open('Producto creado exitosamente', '¡Genial!', { duration: 3000 });
        this.router.navigate(['/library-objects', newObject.library_object_id]);
      },
      error: (err) => {
        console.error('Error al crear el producto:', err);
        this.snackBar.open('Error al crear el producto. Inténtalo de nuevo.', 'Cerrar', { duration: 5000 });
      }
    });
  }



  public saveClientObject(): void {
    if (this.clientAttributeForm.invalid || this.isSaving) {
      this.snackBar.open('Por favor, completa todos los campos requeridos.', 'Cerrar', { duration: 3000 });
      return;
    }
    this.isSaving = true;

    const formValue = this.clientAttributeForm.getRawValue();
    const payload = {
      model_id: +formValue.model_id,
      client_id: +formValue.client_id,
      sku: formValue.sku,
      name: formValue.name,
      label: formValue.label,
      library_object_id: this.libraryId,
      library_attributes: formValue.library_attributes
    };
    let saveObservable: Observable<unknown>;

    if (this.editingClientAttributeId) {
      saveObservable = this.clientLibraryObjectsService.updateClientLibraryObject(this.editingClientAttributeId, payload);
    } else {
      saveObservable = this.clientLibraryObjectsService.createClientLibraryObject(payload);
    }

    saveObservable.pipe(
      finalize(() => { this.isSaving = false; })
    ).subscribe({
      next: () => {
        const message = this.editingClientAttributeId ? 'Atributo de cliente actualizado' : 'Atributo de cliente creado';
        this.snackBar.open(message, '¡Éxito!', { duration: 3000 });
        this.closeDrawer();
        this.loadClientAttributes(this.libraryId);
      },
      error: (err) => {
        console.error('Error al guardar el atributo de cliente:', err);
        this.snackBar.open('Ocurrió un error al guardar.', 'Cerrar', { duration: 5000 });
      }
    });
  }

  public openProductDrawer(): void {
    this.drawerMode = 'product';
    this.syncFiltersUI(this.libraryAttributesArray);
    this.drawer.open();
  }

  public openClientDrawer(): void {
    this.clientAttributeForm.reset()
    this.clientAttributeForm.patchValue({
      label: this.libraryForm.get('label')?.value,
      name: this.libraryForm.get('name')?.value
    });
    this.clientAttributeForm.get('name')?.disable();
    this.drawerMode = 'client';
    this.editingClientAttributeId = null;
    this.clientLibraryAttributesArray.clear();
    this.filters.forEach(f => {
      f.checked = false;
      f.selectedValue = null;
    });
    this.drawer.open();
  }

  public openEditClientDrawer(clientData: ClientAttributeRecord): void {
    this.drawerMode = 'client';
    this.editingClientAttributeId = clientData.client_library_object_id;

    this.clientAttributeForm.patchValue({
      client_id: clientData.client_id,
      model_id: clientData.model_id,
      name: clientData.name,
      label: clientData.label,
      sku: clientData.sku
    });

    this.clientLibraryAttributesArray.clear();
    clientData.library_attributes.forEach((attr: LibraryAttribute) => {
      const attributeId = attr.library_attribute_id ?? attr.id ?? null;
      this.addAttribute(attributeId);
    });

    this.filters.forEach(filter => {
      const matchingAttr = clientData.library_attributes.find(
        (attr: LibraryAttribute) => attr.library_attribute_type.name === filter.key
      );
      if (matchingAttr) {
        filter.checked = true;
        filter.selectedValue = matchingAttr.library_attribute_id ?? matchingAttr.id ?? null;
        if (filter.subFilterOptions.length === 0) {
          this.fetchFilterOptions(filter);
        }
      } else {
        filter.checked = false;
        filter.selectedValue = null;
      }
    });
    this.drawer.open();
  }

  public closeDrawer(): void {
    this.drawer.close();
  }

  public applyProductAttributes(): void {
    this.libraryForm.markAsDirty();
    this.closeDrawer();
    this.snackBar.open('Atributos listos para guardar. Haz clic en ACTUALIZAR.', 'Entendido', { duration: 3500 });
  }

  public onCheckboxChange(filter: FilterOption, isChecked: boolean): void {
    filter.checked = isChecked;
    if (isChecked) {
      if (filter.subFilterOptions.length === 0) {
        this.fetchFilterOptions(filter);
      }
    } else {
      this.removeAttributeById(filter.selectedValue);
      filter.selectedValue = null;
      filter.searchControl.reset('');
    }
  }

  public onSelectionChange(filter: FilterOption, selectedId: number): void {
    this.removeAttributeById(filter.selectedValue);
    filter.selectedValue = selectedId;
    this.addAttribute(selectedId);
  }

  public onImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.fileToUpload = file;
      const reader = new FileReader();
      reader.onload = () => { this.thumbnailPreview = reader.result; };
      reader.readAsDataURL(file);
    }
  }

  private uploadImageAndGetPath(file: File): Observable<string | null> {
    const formData = new FormData();
    formData.append('files', file, file.name);
    formData.append('folder', 'library-objects');
    formData.append('bucketName', 'assets-wre');

    return this.libraryService.uploadImages(formData).pipe(
      map(response => `https://storage.googleapis.com/assets-wre/${response.folderName}/${response.fileName[0]}`),
      catchError(error => {
        this.thumbnailPreview = ''
        console.error('La subida de la imagen falló, pero se continuará con el guardado.', error);
        this.snackBar.open('No se pudo subir la imagen, se guardaron los otros datos.', 'Cerrar', { duration: 4000 });
        return of(null);
      })
    );
  }

  private loadClientAttributes(objectId: number): void {
    this.isLoadingClientAttributes = true;
    const filterParams = { library_object_id: objectId };
    this.clientLibraryObjectsService.getClientLibraryObjectAll(filterParams).pipe(
      finalize(() => this.isLoadingClientAttributes = false)
    ).subscribe({
      next: (response) => {
        this.clientAttributes = response.data || [];
      },
      error: (err) => {
        this.clientAttributes = [];
        this.snackBar.open('Error al cargar los atributos del cliente.', 'Cerrar', { duration: 3000 });
      }
    });
  }

  private syncFiltersUI(targetFormArray: FormArray): void {
    const selectedAttributeIds = new Set(
      targetFormArray.controls.map(control => control.value.library_attribute_id)
    );

    this.filters.forEach(filter => {
      filter.checked = false;
      filter.selectedValue = null;
      for (const option of filter.subFilterOptions) {
        if (selectedAttributeIds.has(option.id)) {
          filter.checked = true;
          filter.selectedValue = option.id;
          break;
        }
      }
    });
  }

  private addAttribute(attributeId: number | null): void {
    if (attributeId === null) return;
    const group = this.fb.group({ library_attribute_id: [attributeId] });
    const targetArray = this.drawerMode === 'product' ? this.libraryAttributesArray : this.clientLibraryAttributesArray;
    targetArray.push(group);
  }

  private removeAttributeById(attributeId: number | null): void {
    if (attributeId === null) return;
    const targetArray = this.drawerMode === 'product' ? this.libraryAttributesArray : this.clientLibraryAttributesArray;
    const index = targetArray.controls.findIndex(c => c.value.library_attribute_id === attributeId);
    if (index !== -1) {
      targetArray.removeAt(index);
    }
  }

  private fetchFilterOptions(filter: FilterOption): void {
    if (!filter.hasSubFilter || filter.subFilterOptions.length > 0) return;

    filter.isLoading = true;
    this.libraryObjectsService.getAttributesByType(filter.key).subscribe({
      next: (items: Array<{ library_attribute_id: number; name: string }>) => {
        filter.subFilterOptions = items.map(item => ({
          id: item.library_attribute_id,
          name: item.name
        }));
        filter.filteredOptions = [...filter.subFilterOptions];
        filter.isLoading = false;
      },
      error: () => { filter.isLoading = false; }
    });
  }

  private initializeFilters(preselected: { key: string; value: number }[]): void {
    this.filters.forEach(f => { f.checked = false; f.selectedValue = null; });
    this.libraryAttributesArray.clear();
    preselected.forEach(attr => {
      const filterToUpdate = this.filters.find(f => f.key === attr.key);
      if (filterToUpdate) {
        filterToUpdate.checked = true;
        filterToUpdate.selectedValue = attr.value;
        this.addAttribute(attr.value);
        this.fetchFilterOptions(filterToUpdate);
      }
    });
  }


  get assignedModelNames(): string[] {
    const selectedIds = this.libraryForm.get('models')?.value || [];

    if (!this.allModels || selectedIds.length === 0) {
      return [];
    }

    return this.allModels
      .filter(model => selectedIds.includes(model.model_id))
      .map(model => model.name);
  }

}
